import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../context/LanguageContext';
import { Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const EMAILJS_SERVICE_ID = 'service_yum9zzm';
  const EMAILJS_TEMPLATE_ID = 'template_pq9uezq';
  const EMAILJS_PUBLIC_KEY = 'IJ4OjSv0VN_TOgx2C';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const templateParams = {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        title: 'Nuovo contatto dal sito molelabs',
        time: new Date().toLocaleString('it-IT')
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      // Show success toast
      toast({
        title: "Successo!",
        description: t.contact.success,
        duration: 5000,
      });

      // Reset form
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast({
        title: "Errore",
        description: t.contact.error,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-transparent">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4">
            {t.contact.title}
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        {/* Contact Form */}
        <div className="bg-slate-900/70 backdrop-blur-sm rounded-2xl shadow-xl shadow-cyan-500/10 p-8 md:p-12 border border-cyan-500/20">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-200 mb-2">
                {t.contact.name}
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Mario Rossi"
                className="w-full px-4 py-3 border border-slate-700 bg-slate-950/70 text-slate-100 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-200 mb-2">
                {t.contact.email}
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="mario.rossi@email.com"
                className="w-full px-4 py-3 border border-slate-700 bg-slate-950/70 text-slate-100 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-slate-200 mb-2">
                {t.contact.message}
              </label>
              <Textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                placeholder="Raccontaci del tuo progetto..."
                rows={6}
                className="w-full px-4 py-3 border border-slate-700 bg-slate-950/70 text-slate-100 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-200 resize-none"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-950 hover:text-white py-6 text-base font-semibold shadow-lg shadow-cyan-500/30 hover:shadow-xl transition-all duration-200 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t.contact.sending}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Send className="w-5 h-5" />
                  {t.contact.send}
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* Contact Info */}
        <div className="mt-12 text-center">
          <p className="text-slate-300">
            Email:{' '}
            <a
              href="mailto:info@molelabs.eu"
              className="text-cyan-300 hover:text-cyan-200 font-medium transition-colors duration-200"
            >
              info@molelabs.eu
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;
